import type { useAuth } from "@clerk/react";
import { queryOptions, type QueryClient } from "@tanstack/react-query";
import {
  isRdMetricsNotPublished,
  type CompactMetricEnvelope,
  type CompactRdMetrics,
  type MetricEnvelope,
  type MetricName,
  type RdMetrics,
  type RdMetricsNotPublished,
  type RdMetricsResponse,
} from "../../../shared/metrics";
import type { RdListItem } from "../components/v2/rd-list-row";
import { ApiRequestError, NetworkRequestError, authenticatedApi } from "./api";
import { rosterScopeQueryValue, type RosterScope } from "./roster-scope-state";

/*
 * Pure query-options and state code behind `metrics.ts`. Everything here runs
 * without React so the cache behavior (seeding, freshness, lifecycle
 * projection) is testable with a bare QueryClient under `tsx --test`, where the
 * `@/` alias that `use-features.ts` relies on does not resolve. Keep hooks out.
 */

export type {
  CompactMetricEnvelope,
  CompactRdMetrics,
  MetricEnvelope,
  MetricName,
  RdMetrics,
  RdMetricsNotPublished,
  RdMetricsResponse,
};

export type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

/**
 * One row of `GET /api/rds?include=metrics`. Either the latest publication
 * covers this RD and the row carries compact metrics, or it does not and the
 * row carries only the marker — never a zeroed or null-filled envelope, which
 * is what keeps it distinguishable from a metric whose value is null (NO-618).
 */
export type PublishedRosterRow = RdListItem & { metrics: CompactRdMetrics };
export type NotPublishedRosterRow = RdListItem & { notPublished: true };
export type RosterMetricsRow = PublishedRosterRow | NotPublishedRosterRow;

export function isRosterRowNotPublished(
  row: RosterMetricsRow
): row is NotPublishedRosterRow {
  return (row as NotPublishedRosterRow).notPublished === true;
}

/** Metrics change at most daily; an hour of reuse keeps Roster -> Snapshot free of refetches. */
export const METRICS_CACHE_MS = 60 * 60_000;

const METRICS = "metrics";

/**
 * Query keys, all under `["metrics", ...]` so they never collide with the
 * roster's `["rds", ...]` keys. The detail key extends the summary key so an
 * invalidation of one RD's summary also reaches its full history.
 */
export const metricsKeys = {
  all: [METRICS] as const,
  rd: (rdId: string) => [METRICS, "rd", rdId] as const,
  rdDetail: (rdId: string) => [METRICS, "rd", rdId, "detail"] as const,
  roster: (scope: RosterScope) =>
    [METRICS, "roster", rosterScopeQueryValue(scope)] as const,
};

export function rdMetricsPath(rdId: string): string {
  return `/api/rds/${encodeURIComponent(rdId)}/metrics`;
}

export function rosterMetricsPath(scope: RosterScope): string {
  return `/api/rds?include=metrics&supervisor=${encodeURIComponent(rosterScopeQueryValue(scope))}`;
}

/** Drops detail periods; every other field (nulls and zeros included) is kept as-is. */
export function toCompactRdMetrics(full: RdMetrics): CompactRdMetrics {
  const metrics = {} as Record<MetricName, CompactMetricEnvelope>;

  for (const name of Object.keys(full.metrics) as MetricName[]) {
    const compact: CompactMetricEnvelope & { periods?: unknown } = {
      ...full.metrics[name],
    };
    delete compact.periods;
    metrics[name] = compact;
  }

  return {
    version: full.version,
    rdId: full.rdId,
    dataThrough: full.dataThrough,
    metrics,
  };
}

/*
 * Completeness model: the summary key holds `CompactRdMetrics` and the detail
 * key holds the full `RdMetrics` with periods. Roster seeding writes only summary
 * keys, so a fresh detail entry is never downgraded by a later roster batch. A
 * summary miss reads a fresh detail entry when one exists (no request) and
 * otherwise fetches the detail endpoint (there is no summary-only endpoint),
 * filing the full result under the detail key as a by-product. A detail demand
 * always fetches when its own key is empty, even after roster seeding.
 */

/**
 * Registers the metrics freshness and retention window as the QueryClient's
 * defaults for every `["metrics", ...]` key. `setQueryData` builds an entry from
 * those defaults, not from any observer's options, so without this a seeded
 * summary nobody has mounted yet would be garbage-collected after TanStack's
 * five-minute default and the Snapshot would refetch. Idempotent per client.
 */
export function ensureMetricsQueryDefaults(queryClient: QueryClient): void {
  queryClient.setQueryDefaults(metricsKeys.all, {
    staleTime: METRICS_CACHE_MS,
    gcTime: METRICS_CACHE_MS,
  });
}

/**
 * Clears every cached metrics read after a Functional form profile save
 * (NO-667). Billable hours is graded at read time against the saved contract
 * minimum, so any cached grade may now be stale. Invalidation alone is not
 * enough: a summary miss is served from a fresh cached detail by its
 * `dataUpdatedAt`, without consulting invalidation, so the entries are reset
 * to empty and the next read fetches.
 */
export function resetMetricsQueries(queryClient: QueryClient): Promise<void> {
  return queryClient.resetQueries({ queryKey: metricsKeys.all });
}

/** Seeds one summary per roster row. Call only with a successful roster response. */
export function seedRdSummariesFromRoster(
  queryClient: QueryClient,
  rows: readonly RosterMetricsRow[],
  updatedAt: number
): void {
  ensureMetricsQueryDefaults(queryClient);

  for (const row of rows) {
    // An unpublished row carries no envelopes to seed; its Snapshot reads the
    // marker from its own request.
    if (isRosterRowNotPublished(row)) continue;

    queryClient.setQueryData(metricsKeys.rd(row.id), row.metrics, { updatedAt });
  }
}

function freshDetail(
  queryClient: QueryClient,
  rdId: string,
  now: number
): RdMetricsResponse | undefined {
  const state = queryClient.getQueryState<RdMetricsResponse>(
    metricsKeys.rdDetail(rdId)
  );
  if (!state || state.data === undefined) return undefined;

  return now - state.dataUpdatedAt < METRICS_CACHE_MS ? state.data : undefined;
}

export interface MetricsQueryContext {
  getToken: ClerkGetToken;
  queryClient: QueryClient;
  now?: () => number;
}

async function readRdSummary(
  rdId: string,
  context: MetricsQueryContext
): Promise<CompactRdMetrics | RdMetricsNotPublished> {
  const now = context.now ?? Date.now;
  const cached = freshDetail(context.queryClient, rdId, now());
  // A not-published body has no envelopes to compact; it passes through whole.
  if (cached)
    return isRdMetricsNotPublished(cached) ? cached : toCompactRdMetrics(cached);

  const full = await authenticatedApi<RdMetricsResponse>(
    context.getToken,
    rdMetricsPath(rdId)
  );
  ensureMetricsQueryDefaults(context.queryClient);
  context.queryClient.setQueryData(metricsKeys.rdDetail(rdId), full, {
    updatedAt: now(),
  });

  return isRdMetricsNotPublished(full) ? full : toCompactRdMetrics(full);
}

async function readRdDetail(
  rdId: string,
  context: MetricsQueryContext
): Promise<RdMetricsResponse> {
  const now = context.now ?? Date.now;
  const full = await authenticatedApi<RdMetricsResponse>(
    context.getToken,
    rdMetricsPath(rdId)
  );
  // The summary is a projection of this response, so it is fresh too.
  ensureMetricsQueryDefaults(context.queryClient);
  context.queryClient.setQueryData(
    metricsKeys.rd(rdId),
    isRdMetricsNotPublished(full) ? full : toCompactRdMetrics(full),
    { updatedAt: now() }
  );

  return full;
}

export function rdSummaryQueryOptions(rdId: string, context: MetricsQueryContext) {
  return queryOptions({
    queryKey: metricsKeys.rd(rdId),
    queryFn: () => readRdSummary(rdId, context),
    staleTime: METRICS_CACHE_MS,
    gcTime: METRICS_CACHE_MS,
  });
}

export function rdDetailQueryOptions(rdId: string, context: MetricsQueryContext) {
  return queryOptions({
    queryKey: metricsKeys.rdDetail(rdId),
    queryFn: () => readRdDetail(rdId, context),
    staleTime: METRICS_CACHE_MS,
    gcTime: METRICS_CACHE_MS,
  });
}

/**
 * One options object whose data type is the union of both completeness levels,
 * so a hook can switch between summary and detail without changing its
 * `useQuery` call shape. Tests use the two typed builders above.
 */
export function rdMetricsQueryOptions(
  rdId: string,
  context: MetricsQueryContext,
  detail: boolean
) {
  return queryOptions<RdMetrics | CompactRdMetrics | RdMetricsNotPublished>({
    queryKey: detail ? metricsKeys.rdDetail(rdId) : metricsKeys.rd(rdId),
    queryFn: () =>
      detail ? readRdDetail(rdId, context) : readRdSummary(rdId, context),
    staleTime: METRICS_CACHE_MS,
    gcTime: METRICS_CACHE_MS,
  });
}

export function rosterMetricsQueryOptions(
  scope: RosterScope,
  context: MetricsQueryContext
) {
  const now = context.now ?? Date.now;

  return queryOptions({
    queryKey: metricsKeys.roster(scope),
    queryFn: async () => {
      const response = await authenticatedApi<{ data: RosterMetricsRow[] }>(
        context.getToken,
        rosterMetricsPath(scope)
      );
      // Seeding happens inside the queryFn (TanStack v5 has no per-query
      // onSuccess) and only on this success path; a rejection never reaches it.
      seedRdSummariesFromRoster(context.queryClient, response.data, now());

      return response.data;
    },
    staleTime: METRICS_CACHE_MS,
    gcTime: METRICS_CACHE_MS,
  });
}

// --- Lifecycle state --------------------------------------------------------

export interface MetricsAuthInput {
  isLoaded: boolean;
  isSignedIn: boolean;
}

export interface MetricsFeatureInput {
  status: "pending" | "error" | "success";
  /** True only when the server evaluated the metrics flag on. */
  flagOn: boolean;
}

export interface MetricsQueryInput<T> {
  status: "pending" | "error" | "success";
  data: T | undefined;
  error: unknown;
  refetch?: () => unknown;
}

export type MetricsErrorKind =
  | "not_found"
  | "access_denied"
  | "bad_request"
  | "network"
  | "server"
  | "unknown";

export type MetricsState<T> =
  | { status: "loading" }
  | { status: "disabled" }
  /** The read succeeded; the latest publication has no record for this RD. */
  | { status: "not_published" }
  | {
      status: "error";
      error: { kind: MetricsErrorKind; cause: unknown };
      retry?: () => void;
    }
  | { status: "ready"; data: T };

/** Only a signed-in viewer with the flag confirmed on may issue a metrics request. */
export function metricsQueryEnabled(input: {
  auth: MetricsAuthInput;
  features: MetricsFeatureInput;
}): boolean {
  if (!input.auth.isLoaded || !input.auth.isSignedIn) return false;

  return input.features.status === "success" && input.features.flagOn;
}

/** The server's feature gate answers 404 `{ error: "Not found" }`; a missing RD says so instead. */
export function isMetricsFeatureGateError(error: unknown): boolean {
  if (!(error instanceof ApiRequestError) || error.status !== 404) return false;

  const body = error.body;

  return (
    typeof body === "object" &&
    body !== null &&
    (body as { error?: unknown }).error === "Not found"
  );
}

export function classifyMetricsError(error: unknown): MetricsErrorKind {
  if (error instanceof NetworkRequestError) return "network";

  if (!(error instanceof ApiRequestError)) return "unknown";

  if (error.status === 404) return "not_found";

  if (error.status === 403) return "access_denied";

  if (error.status === 400) return "bad_request";

  if (error.status >= 500) return "server";

  return "unknown";
}

/**
 * True only for a single RD's not-published body. Roster data is an array and
 * carries its markers per row, so it never answers here.
 */
function isNotPublishedResponse(data: unknown): boolean {
  if (typeof data !== "object" || data === null) return false;

  if (Array.isArray(data)) return false;

  return (data as { notPublished?: unknown }).notPublished === true;
}

/**
 * Folds auth, feature-flag, and query lifecycles into one state the UI renders
 * as data. Current inputs win over cached data: a flag that turned off or a
 * feature read that failed yields `disabled` even when metrics sit in the cache.
 */
export function projectMetricsState<T>(input: {
  auth: MetricsAuthInput;
  features: MetricsFeatureInput;
  query: MetricsQueryInput<T>;
}): MetricsState<T> {
  const { auth, features, query } = input;
  if (!auth.isLoaded || !auth.isSignedIn) return { status: "loading" };

  if (features.status === "pending") return { status: "loading" };

  if (features.status === "error" || !features.flagOn) return { status: "disabled" };

  if (query.status === "error") {
    if (isMetricsFeatureGateError(query.error)) return { status: "disabled" };

    return {
      status: "error",
      error: { kind: classifyMetricsError(query.error), cause: query.error },
      retry: query.refetch
        ? () => {
            void query.refetch?.();
          }
        : undefined,
    };
  }

  if (query.status === "success" && query.data !== undefined) {
    if (isNotPublishedResponse(query.data)) return { status: "not_published" };

    return { status: "ready", data: query.data };
  }

  return { status: "loading" };
}
